import { formatDate } from 'lib/helpers/dateUtils';
import { ia, isPrimitiveValue, mString, singularize, snakeToTitleCase } from 'lib/helpers/utility';

import { getOrdinal } from './getOrdinal';
import { DATE_TIME_FIELDS, FIELD_LABEL_MAP, VALUE_ONLY_KEYS } from './constants';

// Matches enum-like values: lowercase letters/digits/underscores, no spaces (e.g. "in_person", "virtual")
const SNAKE_CASE_PATTERN = /^[a-z][a-z0-9_]*$/;
const DATA_IMAGE_PATTERN = /^data:image\/(png|jpe?g|gif|webp);base64,[a-z0-9+/]+=*$/i;

const AMOUNT_KEYS = ['amount', 'cents']; // Fields containing these substrings will be formatted as amounts
const AMOUNT_FIELDS = [
  'pt_balance',
  'tax',
  'charge',
  'insurance_payment',
  'est_patient_portion',
  'service_discount',
  'balance',
  'unit_price',
  'product_total'
]; // Exact field names to be formatted as amounts
const AMOUNT_SUFFIXES = [
  '_charge',
  '_service_discount',
  '_insurance_payment',
  '_est_patient_portion',
  '_unit_price',
  '_product_total'
]; // Dynamic keys ending with these are also amounts
const PERCENT_SUFFIXES = ['_state_tax', '_local_tax']; // Dynamic keys ending with these are also percent
const NON_BASIC_PERCENT_FIELDS = [
  'percentage',
  'local_tax_rate',
  'state_tax_rate',
  'state_tax',
  'local_tax'
]; // Basis points fields (stored as 0-10000, displayed as 0-100%)
const BASIC_PERCENT_FIELDS = []; // Simple percentage fields (stored as 0-100, displayed with % suffix)

const isNumber = (value) => value !== '' && value !== null && !isNaN(value);

const checkIsAmount = (key, value) =>
  !key?.includes('_id') &&
  !isNaN(value) &&
  (AMOUNT_FIELDS.includes(key) ||
    AMOUNT_KEYS.some((field) => key.includes(field)) ||
    AMOUNT_SUFFIXES.some((suffix) => key.endsWith(suffix)));

const checkIsPercent = (key, value, fields = []) =>
  !key?.includes('_id') &&
  !isNaN(value) &&
  (fields.includes(key) || PERCENT_SUFFIXES.some((suffix) => key.endsWith(suffix)));

const handleBoolean = (val) => (val ? 'Yes' : 'No');

const handleObject = (obj, parentKey, timezone) =>
  Object.keys(obj)
    .map((key) => {
      const value = obj[key];
      if (value === undefined || value === null) return null;
      const formattedKey = formatLabel(key);
      const renderedValue = renderValue({ key: key || parentKey, value, timezone });

      return formattedKey ? `${formattedKey} ${renderedValue}` : renderedValue;
    })
    .filter(Boolean)
    .join('<br/>');

export const checkIsNestedKey = (key, obj) => {
  const objKeys = Object.keys(obj || {});

  if (objKeys.includes(key)) {
    return false;
  }

  const hasNestedChildren = objKeys.some((field) => field.startsWith(`${key}.`));
  if (hasNestedChildren) {
    return true;
  }

  const hasPrimaryValues = obj && Object.values(obj || {}).every(isPrimitiveValue);

  return !hasPrimaryValues && key.includes('.') && key.split('.').some((k) => isNumber(k));
};

export const renderValue = ({ key, value, timezone }) => {
  if (value === undefined || value === null) {
    return 'blank';
  }

  if (typeof value === 'object') {
    const objValue = handleObject(value, key, timezone);

    return objValue;
  }

  if (typeof value === 'boolean') {
    return handleBoolean(value);
  }

  if (DATE_TIME_FIELDS.includes(key) && value) {
    return formatDate(value, timezone, 'MMM d, yyyy h:mma') || value;
  }

  const isNonBasicPercent = checkIsPercent(key, value, NON_BASIC_PERCENT_FIELDS);
  if (isNonBasicPercent) {
    return `${Number(value) / 100}%`;
  }

  const isBasicPercent = checkIsPercent(key, value, BASIC_PERCENT_FIELDS);
  if (isBasicPercent) {
    return `${value}%`;
  }

  const isAmount = checkIsAmount(key, value);
  if (isAmount) {
    return mString(value);
  }

  // Drawn signatures are stored as image data; as text they are one unbreakable line.
  if (typeof value === 'string' && DATA_IMAGE_PATTERN.test(value)) {
    return `<img src="${value}" alt="Image" class="inline-block h-10 max-w-full rounded border border-solid border-neutral-200 bg-white object-contain align-middle" />`;
  }

  if (
    typeof value === 'string' &&
    !VALUE_ONLY_KEYS.includes(key) &&
    SNAKE_CASE_PATTERN.test(value)
  ) {
    const spaced = value.replace(/_/g, ' ').replace(/cancelled/gi, 'canceled');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  return value || 'blank';
};

export const formatLabel = (key) => {
  if (FIELD_LABEL_MAP[key]) return FIELD_LABEL_MAP[key];

  const leafKey = String(key || '')
    .split('.')
    .pop();
  if (leafKey !== key && FIELD_LABEL_MAP[leafKey]) return FIELD_LABEL_MAP[leafKey];

  if (checkIsNestedKey(key)) {
    const keys = key.split('.');

    let prefix = '';
    const numericIndex = keys.findIndex((k) => isNumber(k));

    if (numericIndex > 0) {
      prefix = keys[numericIndex - 1];
    } else {
      // Fallback: use the non-numeric key or first key
      prefix = keys.find((k) => !isNumber(k)) || keys[0] || '';
    }

    const numIndex = keys[numericIndex];

    const ordinal = getOrdinal(numIndex);

    return `${snakeToTitleCase(ordinal)} ${snakeToTitleCase(singularize(prefix))}`;
  }

  // replace the dot with a space
  key = key.replace('.', ' ');

  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase());
};

export const MYRIAD_STAFF_LABEL = 'Myriad staff';
export const MYRIAD_STAFF_AVATAR_ID = -1;
export const AUTOMATION_LABEL = 'Automation';
export const AUTOMATION_AVATAR_ID = -2;

// Automation wins over the creator: created_by still holds whoever triggered the
// run, but they didn't author the value.
export const getCreatorDisplayName = ({ creator, isAdminAction, isAutomation, isViewerAdmin }) => {
  if (isAutomation) return AUTOMATION_LABEL;
  if (isAdminAction && !isViewerAdmin) return MYRIAD_STAFF_LABEL;

  const name = creator ? `${creator.f_name} ${creator.l_name}`.trim() : null;

  return name || (isViewerAdmin ? 'Unknown' : MYRIAD_STAFF_LABEL);
};

export const getCreatorAvatarProps = ({ creator, isMasked, isAutomation }) => {
  if (isAutomation) {
    return {
      id: AUTOMATION_AVATAR_ID,
      fullName: AUTOMATION_LABEL
    };
  }

  if (isMasked) {
    return {
      id: MYRIAD_STAFF_AVATAR_ID,
      fullName: MYRIAD_STAFF_LABEL
    };
  }

  return {
    id: creator?.id,
    firstName: creator?.f_name,
    lastName: creator?.l_name,
    image: creator?.profile_photo
  };
};

export const getArticle = (word) => (/^[aeiou]/i.test(word || '') ? 'an' : 'a');

export const formatReplyTimestamp = (replyDate, parentDate, timezone, formatDateFn) => {
  if (!replyDate) return '';

  const replyDay = formatDateFn(replyDate, timezone, 'yyyy-MM-dd');
  const parentDay = parentDate ? formatDateFn(parentDate, timezone, 'yyyy-MM-dd') : null;
  const time = formatDateFn(replyDate, timezone, 'h:mma');

  if (parentDay && replyDay === parentDay) return time;

  const today = formatDateFn(new Date(), timezone, 'yyyy-MM-dd');
  const yesterday = formatDateFn(
    new Date(new Date().setDate(new Date().getDate() - 1)),
    timezone,
    'yyyy-MM-dd'
  );

  if (replyDay === today) return `Today, ${time}`;
  if (replyDay === yesterday) return `Yesterday, ${time}`;

  return `${formatDateFn(replyDate, timezone, 'MMM d')}, ${time}`;
};

export const isEmptyOrBlank = (data) => {
  if (data === null) return true;

  // If it's an array, check each element
  if (ia(data)) {
    return data.every((item) => isEmptyOrBlank(item));
  }

  // If it's an object, check all its values
  if (typeof data === 'object') {
    const values = Object.values(data);
    return values.every((value) => isEmptyOrBlank(value));
  }

  // Check primitive value
  const strValue = String(data).trim().toLowerCase();
  return strValue === '' || strValue === 'blank' || strValue === 'null' || strValue === 'undefined';
};
